
import React from 'react';
import { 
  BookOpen, 
  ChevronRight, 
  Wallet, 
  PieChart, 
  TrendingUp, 
  ShieldCheck, 
  ArrowLeft, 
  Plus, 
  Download, 
  Calendar,
  Info,
  Zap,
  Heart,
  Target,
  History,
  LayoutGrid,
  ClipboardList,
  PenTool,
  HelpCircle
} from 'lucide-react';

interface UserManualProps {
  onBack: () => void;
}

export const UserManual: React.FC<UserManualProps> = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-10 animate-fade-in pb-32">
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-500 hover:text-electric transition-colors font-bold text-sm"
        >
          <ArrowLeft size={18} /> Back to App
        </button>
        <div className="flex items-center gap-3">
          <BookOpen className="text-electric" size={24} />
          <h1 className="text-2xl font-black text-navy tracking-tight">StackUp User Manual</h1>
        </div>
      </div>

      {/* Introduction */}
      <section className="mb-16">
        <div className="bg-electric/5 border border-electric/10 rounded-4xl p-8 md:p-12">
          <h2 className="text-3xl font-black text-navy mb-6 leading-tight">
            Welcome to your new <span className="text-electric">Financial Command Center</span>.
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            StackUp isn't just a spreadsheet; it's a tool designed to help you see your money clearly, plan for the future, and build lasting wealth. We've made it simple to use, even if you're not a "numbers person."
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="bg-white p-3 rounded-2xl shadow-sm h-fit">
                <Heart className="text-rose-500" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-navy mb-1">Friendly & Simple</h3>
                <p className="text-sm text-slate-500">No complex jargon. Just clear insights into your habits.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-white p-3 rounded-2xl shadow-sm h-fit">
                <ShieldCheck className="text-emerald-500" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-navy mb-1">Private & Secure</h3>
                <p className="text-sm text-slate-500">Your data stays on your device. We don't see your money.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Start? */}
      <section className="mb-20">
        <h2 className="text-2xl font-black text-navy mb-8 flex items-center gap-3">
          <Target className="text-electric" size={24} /> Where should I start?
        </h2>
        <div className="space-y-4">
          <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm">
            <h3 className="font-bold text-navy mb-2">Goal: I want to track my daily spending</h3>
            <p className="text-sm text-slate-500 mb-4">Start with the <strong>Ledger</strong> tab. Add your income first, then log your daily expenses as they happen.</p>
            <div className="flex items-center gap-2 text-xs font-bold text-electric uppercase tracking-widest">
              Go to: Ledger <ChevronRight size={12} />
            </div>
          </div>
          <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm">
            <h3 className="font-bold text-navy mb-2">Goal: I want to save more money</h3>
            <p className="text-sm text-slate-500 mb-4">Start with the <strong>Planner</strong> tab. Set limits for categories like "Dining Out" or "Shopping". Then check the <strong>Home</strong> tab to see if you're staying under your limits.</p>
            <div className="flex items-center gap-2 text-xs font-bold text-electric uppercase tracking-widest">
              Go to: Planner <ChevronRight size={12} />
            </div>
          </div>
          <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm">
            <h3 className="font-bold text-navy mb-2">Goal: I want to see my total wealth</h3>
            <p className="text-sm text-slate-500 mb-4">Start with the <strong>Wealth</strong> tab. Add your bank balances (Assets) and any loans (Liabilities). Your "Net Worth" will be calculated automatically.</p>
            <div className="flex items-center gap-2 text-xs font-bold text-electric uppercase tracking-widest">
              Go to: Wealth <ChevronRight size={12} />
            </div>
          </div>
        </div>
      </section>

      {/* How to Navigate */}
      <section className="mb-20 bg-navy rounded-4xl p-8 md:p-12 text-white">
        <h2 className="text-2xl font-black mb-8 flex items-center gap-3">
          <Zap className="text-electric" size={24} /> How to Navigate
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <LayoutGrid size={20} className="text-electric" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Home (Dashboard)</h4>
                <p className="text-xs text-slate-400">Your birds-eye view. See your health score, AI tips, and quick summaries.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <ClipboardList size={20} className="text-electric" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Ledger</h4>
                <p className="text-xs text-slate-400">The daily log. Add every cent that comes in or goes out here.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-electric" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Wealth</h4>
                <p className="text-xs text-slate-400">The big picture. Track your total value, assets, and debts.</p>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <PenTool size={20} className="text-electric" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Planner</h4>
                <p className="text-xs text-slate-400">The blueprint. Set your monthly spending limits and goals.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <PieChart size={20} className="text-electric" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Report</h4>
                <p className="text-xs text-slate-400">The review. See charts and deep dives into your spending habits.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <HelpCircle size={20} className="text-electric" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Help Icon (Top Right)</h4>
                <p className="text-xs text-slate-400">Quick tips. Click the question mark anywhere for instant help.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
        <div className="space-y-8">
          <ManualSection 
            icon={<Wallet className="text-electric" />}
            title="1. Tracking Your Money"
            items={[
              "Adding your first income",
              "Logging daily expenses",
              "Setting up recurring bills",
              "Importing bank statements (CSV)"
            ]}
          />
          <ManualSection 
            icon={<PieChart className="text-sky" />}
            title="2. Planning Your Budget"
            items={[
              "Setting monthly limits",
              "Categorizing your spending",
              "Understanding the 'Surplus'"
            ]}
          />
        </div>
        <div className="space-y-8">
          <ManualSection 
            icon={<TrendingUp className="text-emerald-500" />}
            title="3. Building Wealth"
            items={[
              "What is 'Net Worth'?",
              "Tracking Assets vs. Liabilities",
              "Using the Investment Simulator"
            ]}
          />
          <ManualSection 
            icon={<Zap className="text-amber-500" />}
            title="4. Safety Nets"
            items={[
              "The Emergency Fund (Bunker)",
              "Legacy Planning",
              "Financial Health Score"
            ]}
          />
        </div>
      </div>

      {/* Detailed Content */}
      <div className="space-y-24">
        {/* Section 1 */}
        <div id="tracking" className="space-y-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy text-white rounded-2xl flex items-center justify-center font-black text-xl">1</div>
            <h2 className="text-3xl font-black text-navy">Tracking Your Money</h2>
          </div>
          <p className="text-slate-600 text-lg">
            The foundation of StackUp is the <strong>Ledger</strong>. Think of this as your digital checkbook.
          </p>
          
          <div className="grid grid-cols-1 gap-6">
            <FeatureDetail 
              title="Adding Entries"
              desc="Click the 'Add Transaction' button. Give it a name (like 'Grocery Store'), enter the amount, and choose if it's money coming in (Income) or going out (Expense)."
              icon={<Plus size={18} />}
            />
            <FeatureDetail 
              title="Recurring Bills"
              desc="Have a monthly rent or Netflix subscription? Mark it as 'Recurring'. StackUp will automatically add it for you every month so you don't have to."
              icon={<Calendar size={18} />}
            />
            <FeatureDetail 
              title="Importing Data"
              desc="If you have a lot of transactions, you can download a 'CSV' file from your bank and upload it here. StackUp will try to read the dates and amounts automatically."
              icon={<Download size={18} />}
            />
          </div>
        </div>

        {/* Section 2 */}
        <div id="budgeting" className="space-y-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy text-white rounded-2xl flex items-center justify-center font-black text-xl">2</div>
            <h2 className="text-3xl font-black text-navy">Planning Your Budget</h2>
          </div>
          <p className="text-slate-600 text-lg">
            Budgeting isn't about restriction; it's about <strong>intention</strong>. Use the Planner to decide where your money goes.
          </p>
          <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
            <h3 className="font-bold text-navy mb-4 flex items-center gap-2">
              <Target className="text-electric" size={20} /> How to set a limit:
            </h3>
            <ol className="space-y-4 text-slate-600">
              <li className="flex gap-3">
                <span className="font-bold text-electric">A.</span>
                Go to the 'Planner' screen.
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-electric">B.</span>
                Find a category (like 'Food' or 'Transport').
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-electric">C.</span>
                Enter how much you <em>want</em> to spend this month.
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-electric">D.</span>
                The Dashboard will show a progress bar. If it turns red, you've gone over your plan!
              </li>
            </ol>
          </div>
        </div>

        {/* Section 3 */}
        <div id="wealth" className="space-y-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy text-white rounded-2xl flex items-center justify-center font-black text-xl">3</div>
            <h2 className="text-3xl font-black text-navy">Building Wealth</h2>
          </div>
          <p className="text-slate-600 text-lg">
            StackUp helps you look at the "Big Picture" through the <strong>Wealth Analyzer</strong>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 bg-emerald-50 rounded-3xl border border-emerald-100">
              <h4 className="font-bold text-emerald-900 mb-2">Assets</h4>
              <p className="text-sm text-emerald-700">Things you OWN that have value. Bank accounts, your car, your house, or investments.</p>
            </div>
            <div className="p-6 bg-rose-50 rounded-3xl border border-rose-100">
              <h4 className="font-bold text-rose-900 mb-2">Liabilities</h4>
              <p className="text-sm text-rose-700">Money you OWE. Credit card debt, loans, or mortgages.</p>
            </div>
          </div>
          <div className="p-8 bg-navy text-white rounded-4xl">
            <div className="flex items-center gap-3 mb-4">
              <Info className="text-electric" />
              <h4 className="font-bold">What is Net Worth?</h4>
            </div>
            <p className="text-slate-300 leading-relaxed">
              Net Worth is simply <strong>Assets minus Liabilities</strong>. It's the single best number to track if you want to know if you're getting richer over time. If this number is going up, you're winning!
            </p>
          </div>
        </div>

        {/* Section 4 */}
        <div id="safety" className="space-y-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy text-white rounded-2xl flex items-center justify-center font-black text-xl">4</div>
            <h2 className="text-3xl font-black text-navy">Safety Nets</h2>
          </div>
          <div className="space-y-6">
            <div className="flex gap-6 p-6 hover:bg-slate-50 rounded-3xl transition-colors">
              <div className="w-12 h-12 bg-sky/20 text-sky rounded-2xl flex items-center justify-center shrink-0">
                <ShieldCheck size={24} />
              </div>
              <div>
                <h4 className="font-bold text-navy text-lg mb-1">The Emergency Bunker</h4>
                <p className="text-slate-500">Life happens. This tool calculates how many months you could survive if your income stopped today. Aim for 3-6 months of "Bunker Time."</p>
              </div>
            </div>
            <div className="flex gap-6 p-6 hover:bg-slate-50 rounded-3xl transition-colors">
              <div className="w-12 h-12 bg-electric/20 text-electric rounded-2xl flex items-center justify-center shrink-0">
                <History size={24} />
              </div>
              <div>
                <h4 className="font-bold text-navy text-lg mb-1">Legacy Planner</h4>
                <p className="text-slate-500">Think long-term. This helps you organize what you want to leave behind for your family or causes you care about.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Closing */}
      <footer className="mt-32 pt-20 border-t border-slate-100 text-center">
        <div className="max-w-xl mx-auto space-y-6">
          <div className="inline-flex p-4 bg-electric/10 rounded-full text-electric mb-4">
            <Zap size={32} />
          </div>
          <h3 className="text-2xl font-black text-navy">Ready to Stack Up?</h3>
          <p className="text-slate-500">
            The best time to start was yesterday. The second best time is right now. Go back to your Dashboard and take control of your future.
          </p>
          <button 
            onClick={onBack}
            className="px-8 py-4 bg-navy text-white rounded-2xl font-bold hover:bg-electric transition-all shadow-xl shadow-navy/10"
          >
            Go to My Dashboard
          </button>
        </div>
      </footer>
    </div>
  );
};

const ManualSection = ({ icon, title, items }: { icon: React.ReactNode, title: string, items: string[] }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3">
      <div className="p-2 bg-slate-50 rounded-xl">{icon}</div>
      <h3 className="font-black text-navy uppercase tracking-wider text-xs">{title}</h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-slate-500 group cursor-default">
          <ChevronRight size={14} className="text-slate-300 group-hover:text-electric transition-colors" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const FeatureDetail = ({ title, desc, icon }: { title: string, desc: string, icon: React.ReactNode }) => (
  <div className="flex gap-6 p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
    <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="space-y-1">
      <h4 className="font-bold text-navy">{title}</h4>
      <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
    </div>
  </div>
);
